import { cookie } from '@app/app/lib/cookie';
import i18n from '@app/app/i18n/config';

import t from './lng';

function getRandomInt(min: number, max: number) {
  return Math.floor(Math.random() * (max - min)) + min;
}

/** SERVICE MESSAGES
 * usage: serviceMessage.showMessage('message text', [type]);
 */
const serviceMessage = (function () {
  const msgWrap = document.createElement('div');
  msgWrap.className = 'service_msgs_wrap';
  document.body.append(msgWrap);

  return {
    showMessage: function (msgText: string, type = '') {
      const newMsg = document.createElement('div'),
        newMsgClose = document.createElement('div');
      newMsg.className = 'service_msg ' + type;
      newMsg.innerText = msgText;
      newMsgClose.className = 'service_msg__close';
      newMsgClose.innerHTML = '&times;';
      newMsgClose.addEventListener('click', function (this: HTMLElement) {
        serviceMessage.removeMessage(this.closest('.service_msg') as HTMLElement);
      });

      newMsg.append(newMsgClose);
      msgWrap.append(newMsg);
      serviceMessage.autoClose(newMsg);
    },
    removeMessage: function (msg: HTMLElement) {
      msg.style.opacity = '0';
      setTimeout(() => {
        // eslint-disable-next-line @typescript-eslint/no-unused-expressions
        msg.parentElement && msg.parentElement.removeChild(msg);
      }, 600);
    },
    autoClose: function (msg: HTMLElement) {
      setTimeout(() => {
        serviceMessage.removeMessage(msg);
      }, 5000);
    },
  };
})();

function wordCase(num: number, titles: string[]) {
  const cases = [2, 0, 1, 1, 1, 2];
  num = Math.abs(num);
  return titles[num % 100 > 4 && num % 100 < 20 ? 2 : cases[num % 10 < 5 ? num % 10 : 5]];
}

function loadScript(src: string, callback?: () => void) {
  if (document.querySelector(`script[src="${src}"]`)) {
    if (typeof callback === 'function') callback();
    return;
  }
  const script = document.createElement('script');
  if (typeof callback === 'function') {
    script.onload = callback;
  }
  script.src = src;
  document.body.appendChild(script);
}

function getFontValue(font: string, shape: number) {
  if (font === t.AutomaticSelection) {
    if ([9, 5, 21, 104].includes(shape)) return 'Nunito';
    else return 'Open Sans';
  } else return font;
}

function xhrHeaders() {
  // Получаем текущий язык из i18n для заголовка Accept-Language
  const currentLanguage =
    typeof window !== 'undefined' && i18n && i18n.language ? i18n.language : 'en';

  return {
    'X-Requested-With': 'XMLHttpRequest',
    'X-CSRFToken': cookie.get('csrftoken') || '',
    'Accept-Language': currentLanguage,
  };
}

function contrast(rgb1: number[], rgb2: number[]) {
  const lum1 = luminance(rgb1[0], rgb1[1], rgb1[2]);
  const lum2 = luminance(rgb2[0], rgb2[1], rgb2[2]);
  const brightest = Math.max(lum1, lum2);
  const darkest = Math.min(lum1, lum2);
  return (brightest + 0.05) / (darkest + 0.05);

  function luminance(r: number, g: number, b: number) {
    const RED = 0.2126;
    const GREEN = 0.7152;
    const BLUE = 0.0722;
    const GAMMA = 2.4;
    const a = [r, g, b].map((v) => {
      v /= 255;
      return v <= 0.03928 ? v / 12.92 : Math.pow((v + 0.055) / 1.055, GAMMA);
    });
    return a[0] * RED + a[1] * GREEN + a[2] * BLUE;
  }
}

const userLocalParams = {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  get: (id: string | number, p: string): any => {
    const params = JSON.parse(window.localStorage.getItem('userParams') || 'null');
    if (params && params[id] && params[id][p]) return params[id][p];
    return null;
  },

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  set: (id: string | number, p: string, v: any) => {
    let params = JSON.parse(window.localStorage.getItem('userParams') || 'null');
    if (params) {
      if (params[id]) {
        params[id][p] = v;
      } else {
        params[id] = {
          [p]: v,
        };
      }
    } else {
      params = {
        [id]: {
          [p]: v,
        },
      };
    }
    window.localStorage.setItem('userParams', JSON.stringify(params));
  },

  delete: (id: string | number, p: string) => {
    const params = JSON.parse(window.localStorage.getItem('userParams') || 'null');
    if (params && params[id] && params[id][p]) {
      delete params[id][p];
      window.localStorage.setItem('userParams', JSON.stringify(params));
    }
  },

  clear: () => {
    window.localStorage.removeItem('userParams');
  },
};

// eslint-disable-next-line @typescript-eslint/no-explicit-any
function isEmpty(obj: any) {
  if (obj === null || obj === undefined) return true;
  if (typeof obj === 'string') return obj.trim() === '';
  if (Array.isArray(obj)) return obj.length === 0;
  if (typeof obj === 'object') return Object.keys(obj).length === 0;
  return false;
}

function getRandomValueFromArray<T>(arr: T[], limit = arr.length): T {
  if (!Array.isArray(arr) || arr.length === 0) {
    throw new Error('Get random value from array: array is empty');
  }

  const effectiveLimit = Math.min(limit, arr.length);

  const randomIndex = Math.floor(Math.random() * effectiveLimit);
  return arr[randomIndex];
}

function wait(ms: number) {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

export {
  getRandomInt,
  serviceMessage,
  wordCase,
  loadScript,
  getFontValue,
  xhrHeaders,
  contrast,
  userLocalParams,
  getRandomValueFromArray,
  isEmpty,
  wait,
};
